import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, FileSearch, ArrowLeft, RotateCcw } from 'lucide-react';
import PageHeader from '../../../components/ui/PageHeader';
import Skeleton from '../../../components/ui/Skeleton';
import Button from '../../../components/ui/Button';
import { useShop } from '../../../hooks/useShop';
import { api } from '../../../api/client';
import DrivePagePreview from '../../../components/ui/DrivePagePreview';

const FIELD_LABELS = {
  vendor_name: 'Vendor name',
  ro_number: 'RO# / PO#',
  total_cost: 'Total cost',
  part_or_sublet: 'Part or sublet?',
  part_number: 'Part number',
  check_recipient: 'Check recipient',
  other: 'Other',
};

export default function DocumentReviewQueuePage() {
  const { date } = useParams();
  const navigate = useNavigate();
  const { currentShopId } = useShop();
  const [tab, setTab] = useState('open');  // 'open' | 'closed'
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null);

  const reload = () => {
    if (!currentShopId || !date) return Promise.resolve();
    setLoading(true);
    setError(null);
    const status = tab === 'open' ? 'PENDING,REOPENED' : 'CONFIRMED,DISMISSED,RESOLVED_BY_EDIT';
    return api.listDocumentReviews({ shopId: currentShopId, status, folderDate: date })
      .then(d => { setReviews(d.reviews || []); setLoading(false); })
      .catch(e => { setError(e.message || String(e)); setLoading(false); });
  };

  useEffect(() => { reload(); }, [currentShopId, date, tab]);

  const handleConfirm = async (review, value) => {
    setActing(review.id);
    try {
      await api.confirmDocumentReview(review.id, value, '');
      await reload();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setActing(null);
    }
  };

  const handleDismiss = async (review) => {
    if (!confirm('Dismiss this review item? It will be marked not actionable.')) return;
    setActing(review.id);
    try {
      await api.dismissDocumentReview(review.id, 'Dismissed by auditor');
      await reload();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setActing(null);
    }
  };

  const handleReopen = async (review) => {
    const reason = prompt('Why are you re-opening this review?');
    if (reason == null) return;
    setActing(review.id);
    try {
      await api.reopenDocumentReview(review.id, reason || 'Re-opened by auditor');
      setTab('open');
      await reload();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setActing(null);
    }
  };

  const goToResults = () => {
    navigate(`/auditor/daily-audit/${date}/cod-checks?tab=needs_action`);
  };

  const dateObj = new Date(date + 'T00:00:00');
  const human = dateObj.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: 'Daily Audit', href: '/auditor/daily-audit' },
          { label: human, href: `/auditor/daily-audit/${date}` },
          { label: 'COD Checks · Document Review' },
        ]}
        title="Document Review"
        description="Confirm or correct items the AI was unsure about. After this gate is clear you can audit the rest of the day."
        actions={
          <Link
            to={`/auditor/daily-audit/${date}`}
            className="text-[13px] text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)] flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to pathways
          </Link>
        }
      />

      <div className="border-b border-[var(--color-warm-200)] mb-4 flex items-center gap-1">
        <button
          onClick={() => setTab('open')}
          className={`px-4 py-2 text-[13px] font-semibold border-b-2 transition-colors ${
            tab === 'open'
              ? 'border-[var(--color-warm-800)] text-[var(--color-warm-900)]'
              : 'border-transparent text-[var(--color-warm-500)] hover:text-[var(--color-warm-700)]'
          }`}
        >
          Open
        </button>
        <button
          onClick={() => setTab('closed')}
          className={`px-4 py-2 text-[13px] font-semibold border-b-2 transition-colors ${
            tab === 'closed'
              ? 'border-[var(--color-warm-800)] text-[var(--color-warm-900)]'
              : 'border-transparent text-[var(--color-warm-500)] hover:text-[var(--color-warm-700)]'
          }`}
        >
          Closed
        </button>
      </div>

      {loading && (
        <div className="space-y-3">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-[var(--color-danger-300)] bg-[var(--color-danger-50)] p-4 text-[var(--color-danger-700)] text-[14px]">
          Failed to load: {error}
        </div>
      )}

      {!loading && !error && reviews.length === 0 && tab === 'open' && (
        <div className="rounded-xl border border-[var(--color-recovery-300)] bg-[var(--color-recovery-50)] p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-[var(--color-recovery-600)] mx-auto mb-3" />
          <h2 className="text-[18px] font-bold text-[var(--color-recovery-700)] mb-1">No Items for Document Review!</h2>
          <p className="text-[14px] text-[var(--color-warm-700)] mb-4">
            The AI was confident on every extraction for this date. Continue to audit results.
          </p>
          <Button onClick={goToResults} className="inline-flex items-center gap-1.5">
            Continue to audit results
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {!loading && !error && reviews.length === 0 && tab === 'closed' && (
        <div className="rounded-xl border border-[var(--color-warm-200)] bg-white p-8 text-center text-[14px] text-[var(--color-warm-700)]">
          No closed reviews for this date.
        </div>
      )}

      {!loading && !error && reviews.length > 0 && tab === 'open' && (
        <>
          <div className="rounded-md bg-[var(--color-action-50)] border border-[var(--color-action-300)] p-3 mb-4 text-[13px] text-[var(--color-action-700)]">
            <strong>{reviews.length} item{reviews.length === 1 ? '' : 's'}</strong> need your confirmation before audit results unlock.
          </div>

          <div className="space-y-4">
            {reviews.map(r => (
              <ReviewCard
                key={r.id}
                review={r}
                onConfirm={handleConfirm}
                onDismiss={handleDismiss}
                acting={acting === r.id}
              />
            ))}
          </div>
        </>
      )}

      {!loading && !error && reviews.length > 0 && tab === 'closed' && (
        <div className="space-y-2">
          {reviews.map(r => (
            <ClosedReviewRow
              key={r.id}
              review={r}
              onReopen={handleReopen}
              acting={acting === r.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ClosedReviewRow({ review, onReopen, acting }) {
  const ck = review.cod_check || {};
  const STATUS_COPY = {
    CONFIRMED: 'Confirmed by auditor',
    DISMISSED: 'Dismissed',
    RESOLVED_BY_EDIT: 'Resolved when fields were edited',
  };
  return (
    <div className="rounded-lg border border-[var(--color-warm-200)] bg-white p-4 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-[var(--color-warm-500)] font-bold">
            {FIELD_LABELS[review.field_to_clarify] || review.field_to_clarify}
          </span>
          <span className="text-[11px] text-[var(--color-warm-500)]">
            · {STATUS_COPY[review.status] || review.status}
          </span>
        </div>
        <h3 className="text-[14px] font-semibold text-[var(--color-warm-900)] mt-1 truncate">
          {ck.matched_vendor_name || ck.payee || '(Unknown vendor)'} · Check #{ck.check_number || '—'}
        </h3>
        {review.confirmed_value && (
          <p className="text-[12px] text-[var(--color-warm-600)] mt-1">
            Value confirmed: <span className="font-mono">{review.confirmed_value}</span>
          </p>
        )}
      </div>
      <button
        onClick={() => onReopen(review)}
        disabled={acting}
        className="text-[13px] font-semibold text-[var(--color-warm-700)] hover:text-[var(--color-warm-900)] flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--color-warm-200)] hover:border-[var(--color-warm-400)] bg-white"
      >
        <RotateCcw className="w-3.5 h-3.5" />
        Re-open
      </button>
    </div>
  );
}

function ReviewCard({ review, onConfirm, onDismiss, acting }) {
  const [value, setValue] = useState(review.ai_proposed_value || '');
  const ck = review.cod_check || {};

  return (
    <div className="rounded-xl border border-[var(--color-warm-200)] bg-white overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        {/* PDF preview */}
        <div className="bg-[var(--color-warm-100)] border-r border-[var(--color-warm-200)] p-3 min-h-[300px] flex items-center justify-center">
          {ck.source_drive_file_id != null && ck.page_index != null && ck.page_index >= 0 ? (
            <DrivePagePreview fileId={ck.source_drive_file_id} pageIndex={ck.page_index} />
          ) : (
            <div className="text-center text-[12px] text-[var(--color-warm-500)]">
              <FileSearch className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No source page available
            </div>
          )}
        </div>

        {/* Review form */}
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-[var(--color-warm-500)] font-bold mb-1">
            {FIELD_LABELS[review.field_to_clarify] || review.field_to_clarify}
            {review.status === 'REOPENED' && (
              <span className="ml-2 text-[var(--color-action-600)]">· Re-opened</span>
            )}
          </div>
          <h3 className="text-[15px] font-semibold text-[var(--color-warm-900)] mb-1">
            {ck.matched_vendor_name || ck.payee || '(Unknown vendor)'} · Check #{ck.check_number || '—'}
          </h3>
          <p className="text-[12px] text-[var(--color-warm-600)] mb-4">
            {review.ai_uncertainty_reason || 'AI is uncertain about this field.'}
          </p>

          <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1">
            Confirmed value
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={acting}
            className="w-full px-3 py-2 rounded-md border border-[var(--color-warm-200)] focus:outline-none focus:border-[var(--color-warm-500)] text-[14px] mb-2"
            placeholder={review.ai_proposed_value || 'Enter the correct value…'}
          />
          {review.ai_proposed_value && review.ai_proposed_value !== value && (
            <p className="text-[11px] text-[var(--color-warm-500)] mb-3">
              AI proposed: <button onClick={() => setValue(review.ai_proposed_value)} className="underline hover:text-[var(--color-warm-800)]"><span className="font-mono">{review.ai_proposed_value}</span></button>
            </p>
          )}

          <div className="flex items-center gap-2 mt-3">
            <Button
              onClick={() => onConfirm(review, value)}
              disabled={acting || !value.trim()}
              className="flex items-center gap-1.5"
            >
              <CheckCircle2 className="w-4 h-4" />
              Confirm
            </Button>
            <button
              onClick={() => onDismiss(review)}
              disabled={acting}
              className="text-[13px] text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)] underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
